import { useState, useEffect } from 'react'
import { User, Booking } from '@/types'
import { collection, query, where, getDocs, orderBy, addDoc, deleteDoc, doc, serverTimestamp, getDoc } from 'firebase/firestore'
import { db } from '@/config/firebase'
import { useAuthStore } from '@/store/authStore'
import { X, Calendar, Phone, Shield, Ban, CheckCircle, User as UserIcon, Mail } from 'lucide-react'
import toast from 'react-hot-toast'
import LoadingState from '@/components/ui/LoadingState'
import EmptyState from '@/components/ui/EmptyState'

interface MemberDetailsModalProps {
    isOpen: boolean
    onClose: () => void
    userId: string
}

export default function MemberDetailsModal({ isOpen, onClose, userId }: MemberDetailsModalProps) {
    const { currentOrg } = useAuthStore()
    const [user, setUser] = useState<User | null>(null)
    const [bookings, setBookings] = useState<Booking[]>([])
    const [loading, setLoading] = useState(true)
    const [isBlocked, setIsBlocked] = useState(false)
    const [blockReason, setBlockReason] = useState('')
    const [showBlockConfirm, setShowBlockConfirm] = useState(false)
    const [blockedId, setBlockedId] = useState<string | null>(null)

    useEffect(() => {
        if (isOpen && userId && currentOrg) {
            loadUserData()
        }
    }, [isOpen, userId, currentOrg])

    const loadUserData = async () => {
        setLoading(true)
        try {
            // 1. Fetch User Data
            const userDoc = await getDoc(doc(db, 'users', userId))
            if (userDoc.exists()) {
                setUser({ id: userDoc.id, ...userDoc.data() } as User)
            }

            // 2. Fetch User Bookings in this Org
            if (currentOrg) {
                const q = query(
                    collection(db, 'bookings'),
                    where('orgId', '==', currentOrg.id),
                    where('userId', '==', userId),
                    orderBy('createdAt', 'desc')
                )
                const bookingsSnap = await getDocs(q)
                setBookings(bookingsSnap.docs.map(d => ({ id: d.id, ...d.data() } as Booking)))

                // 3. Check Blacklist Status
                const blQuery = query(
                    collection(db, 'blacklist'),
                    where('orgId', '==', currentOrg.id),
                    where('userId', '==', userId)
                )
                const blSnap = await getDocs(blQuery)
                if (!blSnap.empty) {
                    setIsBlocked(true)
                    setBlockedId(blSnap.docs[0].id)
                    setBlockReason(blSnap.docs[0].data().reason)
                } else {
                    setIsBlocked(false)
                    setBlockedId(null)
                    setBlockReason('')
                }
            }
        } catch (error) {
            console.error('Erro ao carregar detalhes:', error)
            toast.error('Erro ao carregar informações do membro.')
        } finally {
            setLoading(false)
        }
    }

    const handleBlockUser = async () => {
        if (!currentOrg) return
        try {
            await addDoc(collection(db, 'blacklist'), {
                orgId: currentOrg.id,
                userId: userId,
                reason: blockReason || 'Bloqueio administrativo',
                createdAt: serverTimestamp()
            })
            setIsBlocked(true)
            setShowBlockConfirm(false)
            toast.success('Usuário bloqueado com sucesso.')
            loadUserData() // Reload to get the block ID
        } catch (error) {
            toast.error('Erro ao bloquear usuário.')
        }
    }

    const handleUnblockUser = async () => {
        if (!blockedId) return
        try {
            await deleteDoc(doc(db, 'blacklist', blockedId))
            setIsBlocked(false)
            setBlockedId(null)
            toast.success('Usuário desbloqueado.')
        } catch (error) {
            toast.error('Erro ao desbloquear.')
        }
    }

    if (!isOpen) return null

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-[#001c28]/80 backdrop-blur-sm animate-in fade-in duration-200">
            <div className="bg-white/95 rounded-[2rem] border border-white/20 w-full max-w-4xl max-h-[90vh] shadow-2xl overflow-hidden flex flex-col animate-in zoom-in-95 duration-200">

                {/* Header */}
                <div className="px-8 py-6 border-b border-[#001c28]/5 flex items-center justify-between shrink-0 bg-white relative">
                    <div className="absolute top-0 right-0 w-64 h-full bg-gradient-to-l from-[#d4a94d]/10 to-transparent opacity-50" />
                    <div className="relative z-10">
                        <h2 className="text-2xl font-black text-[#001c28] uppercase italic tracking-tighter leading-none">Detalhes do Membro</h2>
                        <p className="text-[10px] font-bold text-[#d4a94d] uppercase tracking-[0.2em] mt-1">
                            Visão Geral e Histórico
                        </p>
                    </div>
                    <button onClick={onClose} className="relative z-10 p-2 bg-[#001c28]/5 hover:bg-[#001c28]/10 rounded-full text-[#001c28] transition-colors">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-8 custom-scrollbar bg-[#f8fafc]">
                    {loading ? (
                        <div className="flex items-center justify-center p-12">
                            <LoadingState />
                        </div>
                    ) : user ? (
                        <div className="space-y-6">
                            {/* Profile Card */}
                            <div className="bg-white p-6 rounded-[1.5rem] shadow-sm border-2 border-[#001c28]/10 flex flex-col md:flex-row gap-6 items-start relative overflow-hidden">
                                {isBlocked && (
                                    <div className="absolute top-0 right-0 bg-red-500 text-white px-4 py-1 rounded-bl-xl text-[10px] font-black uppercase tracking-widest z-10">
                                        Bloqueado
                                    </div>
                                )}

                                <div className="w-24 h-24 rounded-2xl bg-[#001c28] flex items-center justify-center text-[#d4a94d] shrink-0 relative overflow-hidden shadow-lg border-4 border-white">
                                    {user.photoURL ? (
                                        <img src={user.photoURL} alt={user.displayName} className="w-full h-full object-cover" />
                                    ) : (
                                        <span className="text-3xl font-black">{user.displayName?.charAt(0)}</span>
                                    )}
                                </div>

                                <div className="flex-1 w-full space-y-4">
                                    <div>
                                        <h3 className="text-2xl font-black text-[#001c28] uppercase italic tracking-tight flex items-center gap-3">
                                            {user.displayName}
                                        </h3>
                                        <p className="text-[#001c28]/60 font-medium text-sm flex items-center gap-2">
                                            <Mail size={14} className="text-[#d4a94d]" />
                                            {user.email}
                                        </p>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                        <div className="bg-[#001c28]/[0.02] p-3 rounded-xl border border-[#001c28]/10">
                                            <p className="text-[9px] font-black text-[#001c28]/40 uppercase tracking-widest mb-1">Telefone</p>
                                            <div className="flex items-center gap-2 font-bold text-[#001c28] text-sm">
                                                <Phone className="w-3.5 h-3.5 text-[#d4a94d]" />
                                                {user.phone || '-'}
                                            </div>
                                        </div>
                                        <div className="bg-[#001c28]/[0.02] p-3 rounded-xl border border-[#001c28]/10">
                                            <p className="text-[9px] font-black text-[#001c28]/40 uppercase tracking-widest mb-1">CPF</p>
                                            <div className="flex items-center gap-2 font-bold text-[#001c28] text-sm">
                                                <Shield className="w-3.5 h-3.5 text-[#d4a94d]" />
                                                {user.cpf || '-'}
                                            </div>
                                        </div>
                                        <div className="bg-[#001c28]/[0.02] p-3 rounded-xl border border-[#001c28]/10">
                                            <p className="text-[9px] font-black text-[#001c28]/40 uppercase tracking-widest mb-1">Data de Nascimento</p>
                                            <div className="flex items-center gap-2 font-bold text-[#001c28] text-sm">
                                                <Calendar className="w-3.5 h-3.5 text-[#d4a94d]" />
                                                {user.birthDate ? new Date(user.birthDate).toLocaleDateString() : '-'}
                                            </div>
                                        </div>
                                        <div className="bg-[#001c28]/[0.02] p-3 rounded-xl border border-[#001c28]/10">
                                            <p className="text-[9px] font-black text-[#001c28]/40 uppercase tracking-widest mb-1">ID Interno</p>
                                            <div className="flex items-center gap-2 font-bold text-[#001c28] text-sm">
                                                <UserIcon className="w-3.5 h-3.5 text-[#d4a94d]" />
                                                <span className="font-mono bg-[#001c28]/5 px-2 py-0.5 rounded">{user.identificationCode || 'N/A'}</span>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Address Section */}
                                    {user.address && (
                                        <div className="bg-[#001c28]/[0.02] p-4 rounded-xl border border-[#001c28]/10 mt-2">
                                            <p className="text-[9px] font-black text-[#001c28]/40 uppercase tracking-widest mb-2 flex items-center gap-2">
                                                <Shield className="w-3 h-3 text-[#d4a94d]" />
                                                Endereço
                                            </p>
                                            <div className="text-sm font-medium text-[#001c28] space-y-1">
                                                <p className="font-bold">
                                                    {user.address.street}, {user.address.number} {user.address.complement && `- ${user.address.complement}`}
                                                </p>
                                                <p className="text-[#001c28]/70">
                                                    {user.address.neighborhood} - {user.address.city}/{user.address.state}
                                                </p>
                                                <p className="text-[#001c28]/50 text-xs">CEP: {user.address.zipCode}</p>
                                            </div>
                                        </div>
                                    )}

                                    {/* Actions */}
                                    <div className="pt-2 flex gap-3">
                                        {isBlocked ? (
                                            <button
                                                onClick={handleUnblockUser}
                                                className="bg-green-100 text-green-700 px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-green-200 transition-colors flex items-center gap-2 shadow-sm"
                                            >
                                                <CheckCircle className="w-3.5 h-3.5" />
                                                Desbloquear
                                            </button>
                                        ) : (
                                            <button
                                                onClick={() => setShowBlockConfirm(true)}
                                                className="bg-red-50 text-red-500 px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-red-100 transition-colors flex items-center gap-2 shadow-sm"
                                            >
                                                <Ban className="w-3.5 h-3.5" />
                                                Bloquear Acesso
                                            </button>
                                        )}
                                    </div>

                                    {showBlockConfirm && (
                                        <div className="bg-red-50 p-4 rounded-xl border border-red-100 animate-in fade-in slide-in-from-top-2 mt-2">
                                            <h4 className="font-bold text-red-900 text-xs uppercase mb-2">Confirmar Bloqueio</h4>
                                            <div className="flex gap-2">
                                                <input
                                                    type="text"
                                                    placeholder="Motivo do bloqueio..."
                                                    className="flex-1 bg-white border border-red-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-200"
                                                    value={blockReason}
                                                    onChange={e => setBlockReason(e.target.value)}
                                                />
                                                <button onClick={handleBlockUser} className="bg-red-600 text-white px-4 py-2 rounded-lg text-xs font-bold hover:bg-red-700 shadow-sm uppercase tracking-wide">Confirmar</button>
                                                <button onClick={() => setShowBlockConfirm(false)} className="px-3 text-xs font-bold text-red-400 hover:text-red-700 uppercase">Cancelar</button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Bookings History */}
                            <div className="bg-white p-6 rounded-[1.5rem] shadow-sm border-2 border-[#001c28]/10 flex flex-col">
                                <div className="flex items-center gap-2 pb-4 border-b border-[#001c28]/5 mb-4">
                                    <div className="w-8 h-8 rounded-lg bg-[#001c28]/5 flex items-center justify-center text-[#001c28]">
                                        <Calendar size={16} />
                                    </div>
                                    <h3 className="text-sm font-black text-[#001c28] uppercase tracking-wide">
                                        Histórico de Reservas
                                    </h3>
                                </div>

                                {bookings.length === 0 ? (
                                    <div className="py-8">
                                        <EmptyState title="Nenhuma reserva" description="Este usuário ainda não realizou agendamentos." icon={Calendar} />
                                    </div>
                                ) : (
                                    <div className="rounded-xl border border-[#001c28]/5 overflow-hidden">
                                        <table className="w-full text-sm text-left">
                                            <thead className="bg-[#001c28]/[0.02] border-b border-[#001c28]/5">
                                                <tr>
                                                    <th className="px-6 py-4 text-[9px] font-black uppercase tracking-[0.1em] text-[#001c28]/40">Data</th>
                                                    <th className="px-6 py-4 text-[9px] font-black uppercase tracking-[0.1em] text-[#001c28]/40">Espaço</th>
                                                    <th className="px-6 py-4 text-[9px] font-black uppercase tracking-[0.1em] text-[#001c28]/40">Status</th>
                                                    <th className="px-6 py-4 text-[9px] font-black uppercase tracking-[0.1em] text-[#001c28]/40 text-right">Valor</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-[#001c28]/5">
                                                {bookings.map(booking => (
                                                    <tr key={booking.id} className="hover:bg-[#001c28]/[0.02] transition-colors">
                                                        <td className="px-6 py-4 font-bold text-[#001c28]">{booking.start?.toDate().toLocaleDateString()}</td>
                                                        <td className="px-6 py-4 text-[#001c28]/70">{(booking as any).spaceName || 'Espaço'}</td>
                                                        <td className="px-6 py-4">
                                                            <span className={`px-2 py-0.5 rounded text-[9px] font-black uppercase tracking-wide border ${booking.status === 'CONFIRMED' ? 'bg-green-50 text-green-600 border-green-100' :
                                                                booking.status === 'CANCELED' ? 'bg-red-50 text-red-600 border-red-100' :
                                                                    'bg-[#d4a94d]/10 text-[#d4a94d] border-[#d4a94d]/20'
                                                                }`}>
                                                                {booking.status === 'PENDING_APPROVAL' ? 'Aguardando' :
                                                                    booking.status === 'PENDING_PAYMENT' ? 'Pgto Pendente' :
                                                                        booking.status === 'CONFIRMED' ? 'Confirmado' :
                                                                            booking.status === 'CANCELED' ? 'Cancelado' : booking.status}
                                                            </span>
                                                        </td>
                                                        <td className="px-6 py-4 text-right font-mono font-bold text-[#001c28]">
                                                            R$ {booking.totalPrice?.toFixed(2)}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>
                        </div>
                    ) : (
                        <div className="flex flex-col items-center justify-center h-full text-center py-20 opacity-50">
                            <UserIcon size={48} className="mb-4 text-[#001c28]" />
                            <p className="font-bold text-[#001c28]">Usuário não encontrado</p>
                        </div>
                    )}
                </div>

                {/* Footer can be added here if needed, but close button is at top */}
            </div>
        </div>
    )
}
